import React, { useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import axios from '../../../../library/axios';
import {
  handleJobQueuedSuccess,
  handleJobRequestError,
} from '../../../../helpers/cpanelJobFeedback';
import { USER_SCOPE_CENTRAL, CENTRAL_ROLES, ACTIVE_OPTIONS } from './constants';
import { hasValidationErrors, validateCentralForm, validateHospitalForm } from './validation';

const EditCpanelUserComp = ({
  scope,
  tenantSlug,
  tenants = [],
  meta,
  editUser,
  close,
  fetch,
  onTenantChange,
}) => {
  const isCentral = scope === USER_SCOPE_CENTRAL;
  const [selectedTenant, setSelectedTenant] = useState(tenantSlug || '');
  const [loading, setLoading] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [namaLengkap, setNamaLengkap] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('operator');
  const [idGroups, setIdGroups] = useState('');
  const [idDivision, setIdDivision] = useState('');
  const [nik, setNik] = useState('');
  const [jabatan, setJabatan] = useState('');
  const [active, setActive] = useState('YES');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setSelectedTenant(tenantSlug || '');
  }, [tenantSlug]);

  useEffect(() => {
    if (!editUser) return;
    setUsername(editUser.username || '');
    setFullName(editUser.namaLengkap || editUser.fullName || '');
    setNamaLengkap(editUser.namaLengkap || '');
    setEmail(editUser.email || '');
    setRole(editUser.role || 'operator');
    setIdGroups(editUser.idGroups || '');
    setIdDivision(editUser.idDivision || editUser.risk_division?.id || '');
    setNik(editUser.nik || '');
    setJabatan(editUser.jabatan || '');
    setActive(editUser.active || 'YES');
    setPassword('');
  }, [editUser]);

  const handleUpdate = async (event) => {
    event?.preventDefault();
    const nextErrors = isCentral
      ? validateCentralForm({ username, fullName, password, role }, { isEdit: true })
      : validateHospitalForm({ username, namaLengkap, email, idGroups, password }, { isEdit: true });
    setErrors(nextErrors);
    if (hasValidationErrors(nextErrors)) return;

    setLoading(true);
    const payload = isCentral
      ? {
          scope,
          username: username.trim(),
          password,
          fullName: fullName.trim(),
          email: email.trim(),
          role,
          active,
        }
      : {
          scope,
          tenantSlug: selectedTenant || editUser?.tenantSlug,
          username: username.trim(),
          password,
          namaLengkap: namaLengkap.trim(),
          email: email.trim(),
          idGroups,
          idDivision: idDivision || null,
          nik: nik.trim() || null,
          jabatan: jabatan.trim() || '-',
          active,
        };

    try {
      const res = await axios.patch(`/cpanel/users/update/${editUser.id}`, payload);
      await handleJobQueuedSuccess({
        response: res,
        actionLabel: isCentral ? 'Perubahan pengguna CPanel' : 'Perubahan pengguna rumah sakit',
        close,
        fetch,
      });
    } catch (err) {
      handleJobRequestError(err, 'Gagal memulai perubahan pengguna.');
    } finally {
      setLoading(false);
    }
  };

  if (!editUser) return null;

  return (
    <form noValidate onSubmit={handleUpdate}>
      <div className="mb-3">
        <label className="form-label required">Username</label>
        <input className={`form-control${errors.username ? ' is-invalid' : ''}`} value={username} onChange={(e) => setUsername(e.target.value)} />
      </div>
      <div className="mb-3">
        <label className="form-label">Password baru (kosongkan jika tidak diubah)</label>
        <input type="password" className="form-control" value={password} onChange={(e) => setPassword(e.target.value)} />
      </div>
      {isCentral ? (
        <>
          <div className="mb-3">
            <label className="form-label required">Nama Lengkap</label>
            <input className="form-control" value={fullName} onChange={(e) => setFullName(e.target.value)} />
          </div>
          <div className="mb-3">
            <label className="form-label">Email</label>
            <input className="form-control" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="mb-3">
            <label className="form-label required">Role</label>
            <select className="form-select" value={role} onChange={(e) => setRole(e.target.value)}>
              {CENTRAL_ROLES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
        </>
      ) : (
        <>
          <div className="mb-3">
            <label className="form-label">Tenant / Rumah Sakit</label>
            <select
              className="form-select"
              value={selectedTenant}
              onChange={(e) => {
                setSelectedTenant(e.target.value);
                onTenantChange?.(e.target.value);
              }}
            >
              {tenants.map((t) => (
                <option key={t.id} value={t.slug}>
                  {t.slug} - {t.description}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label required">Nama Lengkap</label>
            <input className="form-control" value={namaLengkap} onChange={(e) => setNamaLengkap(e.target.value)} />
          </div>
          <div className="mb-3">
            <label className="form-label required">Email</label>
            <input className="form-control" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="mb-3">
            <label className="form-label required">Role (Group)</label>
            <select className="form-select" value={idGroups} onChange={(e) => setIdGroups(e.target.value)}>
              <option value="">Pilih role</option>
              {(meta?.groups || []).map((g) => (
                <option key={g.id} value={g.id}>
                  {g.namaGroup}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label">Divisi</label>
            <select className="form-select" value={idDivision} onChange={(e) => setIdDivision(e.target.value)}>
              <option value="">- Kosong -</option>
              {(meta?.divisions || []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.namaDivision}
                </option>
              ))}
            </select>
          </div>
        </>
      )}
      <div className="mb-4">
        <label className="form-label">Status</label>
        <select className="form-select" value={active} onChange={(e) => setActive(e.target.value)}>
          {ACTIVE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      <div className="d-flex gap-2 justify-content-end">
        <Button variant="light" onClick={close} disabled={loading}>
          Batal
        </Button>
        <Button type="submit" variant="primary" disabled={loading}>
          {loading ? 'Memproses...' : 'Simpan'}
        </Button>
      </div>
    </form>
  );
};

export default EditCpanelUserComp;
